// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — own data, phase 4 (docs/AYNI_DATA_INTAKE_PLAN.md, "Access
 * security → People"; contract docs/AYNI_DATA_INTAKE_API.md sections
 * 12 and 15–18): the limits and formats of sharing, step-up sign-in and lab
 * roles that the XR server (`functions/api/data/`) and the browser share.
 * No DOM, no Worker globals.
 *
 * - **Shares.** An invitation binds to a signed-in account (by its email
 *   address; an address with no account yet is bound at its first
 *   sign-in). A link is a random secret that works for any signed-in
 *   account that opens it, at most 30 days, and can be revoked; only its
 *   SHA-256 is stored. A meeting share lasts the meeting (decision O6,
 *   on by default, `DATA_MEETING_SHARES=false` is the kill switch; the
 *   presenter confirms an explicit notice first). Every share is read only: nothing a
 *   share gives can upload, publish, share again or delete.
 * - **Step-up (decision O4, owner 2026-10-06):** risky actions need a
 *   passkey (WebAuthn, user verification required) or, where the account
 *   has no passkey, a fresh one-time code sent to its email address; either
 *   opens a ten-minute window for the session that asked for it.
 * - **Provisional numbers:** invitation lengths (90 days by default, at
 *   most a year) and the cap of active shares per dataset are not in the
 *   plan; they are constants so a decision is a one-line change.
 */

/** A share link works at most this many days (the plan: "at most 30 days"). */
export const SHARE_LINK_MAX_DAYS = 30
export const SHARE_LINK_DEFAULT_DAYS = 7
/** Provisional: an invitation's longest life, and its default. */
export const SHARE_INVITE_MAX_DAYS = 365
export const SHARE_INVITE_DEFAULT_DAYS = 90
/** Provisional: active shares a dataset may have at once. */
export const MAX_ACTIVE_SHARES = 50
/** Most passkeys one account may register. */
export const MAX_PASSKEYS = 10
/** A meeting share never outlives this (a meeting's longest life). */
export const MEETING_SHARE_MAX_MS = 8 * 60 * 60 * 1000
/** After this long, an expired or revoked invitation's email address is cleared (personal data). */
export const SHARE_EMAIL_KEEP_MS = 30 * 24 * 60 * 60 * 1000

export type ShareKind = 'account' | 'link' | 'meeting'

const DAY_MS = 86_400_000

/**
 * The number of days a new share lasts: the default when none is given,
 * else a whole number from 1 to the kind's maximum; null when out of range.
 */
export function shareDays(kind: 'account' | 'link', days: unknown): number | null {
  const max = kind === 'link' ? SHARE_LINK_MAX_DAYS : SHARE_INVITE_MAX_DAYS
  if (days === undefined || days === null) return kind === 'link' ? SHARE_LINK_DEFAULT_DAYS : SHARE_INVITE_DEFAULT_DAYS
  if (typeof days !== 'number' || !Number.isInteger(days) || days < 1 || days > max) return null
  return days
}

/** When a share made at `nowMs` for `days` ends. */
export const shareExpiry = (nowMs: number, days: number): number => nowMs + days * DAY_MS

/** The fragment a share link carries (never a query: a fragment is not sent to any server or in a Referer). */
export const SHARE_LINK_PARAM = 'ayni-share'

/** A share link's secret: 32 random bytes, base64url (43 characters). */
export const SHARE_SECRET = /^[A-Za-z0-9_-]{43}$/
const SHARE_ID_RE = /^sh[0-9a-f]{24}$/

/** The address a share link opens: the site with `#ayni-share=<share id>.<secret>`. */
export function formatShareLink(origin: string, shareId: string, secret: string): string {
  return `${origin.replace(/\/$/, '')}/#${SHARE_LINK_PARAM}=${shareId}.${secret}`
}

/**
 * The share id and secret in a share link, its fragment, or the bare
 * `<id>.<secret>` value; null for anything else.
 */
export function parseShareLink(text: unknown): { id: string; secret: string } | null {
  if (typeof text !== 'string' || text.length > 400) return null
  let value = text.trim()
  const at = value.indexOf(`${SHARE_LINK_PARAM}=`)
  if (at >= 0) value = value.slice(at + SHARE_LINK_PARAM.length + 1).split('&')[0]
  const dot = value.indexOf('.')
  if (dot < 0) return null
  const id = value.slice(0, dot)
  const secret = value.slice(dot + 1)
  return SHARE_ID_RE.test(id) && SHARE_SECRET.test(secret) ? { id, secret } : null
}

/** The text whose SHA-256 names a meeting in a meeting share (the room code itself is never stored). */
export const meetingShareText = (room: string): string => `ayni-meeting|${room}`

// ----------------------------------------------------------------- step-up

/** The actions that need a fresh step-up (the plan's list, plus roles and adding or removing a passkey). */
export const STEP_UP_ACTIONS = ['source.add', 'share.create', 'meeting.share', 'dataset.open', 'dataset.delete', 'exact.export', 'role.change', 'owner.handover', 'passkey.add', 'passkey.remove', 'passkey.keep', 'account.delete'] as const
export type StepUpAction = (typeof STEP_UP_ACTIONS)[number]
export const isStepUpAction = (v: unknown): v is StepUpAction => typeof v === 'string' && (STEP_UP_ACTIONS as readonly string[]).includes(v)

/**
 * Actions that need a step-up of their own (review I-3): proved for that
 * action and spent by it, never covered by the ten-minute window another
 * confirmation opened.
 */
export const STRICT_STEP_UP_ACTIONS: readonly StepUpAction[] = ['passkey.add', 'passkey.remove', 'dataset.open', 'owner.handover', 'account.delete']
export const isStrictStepUp = (a: StepUpAction): boolean => STRICT_STEP_UP_ACTIONS.includes(a)

/** Eight digits (review L-2: with the caps below, about 15 in 10^8 a day for a guesser). */
export const STEP_UP_CODE_DIGITS = 8
export const STEP_UP_CODE_RE = /^\d{8}$/
/** Wrong codes an account may give in 24 hours before emailed step-up is locked for a day (and the account told). */
export const STEP_UP_DAILY_FAILURES = 15
/** How long the "this wasn't me" link mailed with a new passkey works. */
export const PASSKEY_REVOKE_LINK_MS = 7 * 24 * 60 * 60 * 1000
/** A passkey removed with an emailed code (lost passkey) goes only this long after the request. */
export const PASSKEY_REMOVAL_DELAY_MS = 72 * 60 * 60 * 1000
/** The fragment a "this wasn't me" link carries. */
export const PASSKEY_REVOKE_PARAM = 'ayni-passkey-revoke'
/** A code works this long after it is sent. */
export const STEP_UP_CODE_TTL_MS = 10 * 60 * 1000
/** A verified code opens a window this long for its session. */
export const STEP_UP_WINDOW_MS = 10 * 60 * 1000
/** Wrong tries before a code stops working. */
export const STEP_UP_MAX_ATTEMPTS = 5
/** Emailed codes an account may ask for per hour (passkey challenges count against the step-up rate limit only). */
export const STEP_UP_CODES_PER_HOUR = 6

/** A random code of `STEP_UP_CODE_DIGITS` digits, uniformly drawn (rejection sampling, no modulo bias). */
export function newStepUpCode(): string {
  const limit = 10 ** STEP_UP_CODE_DIGITS
  const ceiling = Math.floor(0x1_0000_0000 / limit) * limit
  for (;;) {
    const n = crypto.getRandomValues(new Uint32Array(1))[0]
    if (n < ceiling) return String(n % limit).padStart(STEP_UP_CODE_DIGITS, '0')
  }
}

/** "j•••@example.org": enough for a person to recognise their address, not to read it off a screen. */
export function maskEmail(email: string): string {
  const at = email.indexOf('@')
  if (at <= 0) return '•••'
  return `${email[0]}•••${email.slice(at)}`
}

// ------------------------------------------------------------------- roles

export type LabRole = 'owner' | 'member' | 'viewer'
export const LAB_ROLES: readonly LabRole[] = ['owner', 'member', 'viewer']
export const isLabRole = (v: unknown): v is LabRole => typeof v === 'string' && (LAB_ROLES as readonly string[]).includes(v)

/** What each role may do in its own lab (the plan's "Lab roles"). */
export type LabAction = 'view' | 'upload' | 'share' | 'source' | 'delete' | 'open' | 'members' | 'custody_all'

export function roleAllows(role: LabRole, action: LabAction): boolean {
  switch (action) {
    case 'view':
      return true
    case 'upload':
    case 'share':
    case 'source':
      return role === 'owner' || role === 'member'
    default:
      return role === 'owner'
  }
}
